import {Alert, Badge, Box, Button, Group, Loader, Stack, Text, Title} from '@mantine/core'
import {IconCheck} from '@tabler/icons-react'
import {Link} from '@tanstack/react-router'
import {Fragment, useEffect, useMemo, useState} from 'react'

import {useRentalAssetTypes} from 'src/assets/rental-assets/use-rental-asset-types'
import {useWorkerTypes} from 'src/assets/worker-types/use-worker-types'
import {useCustomer} from 'src/customers/use-customers'
import {
    BuildOutSummaryStats,
    JobsReadySwitch,
    ScheduleBuildOutEditor,
} from 'src/service-schedules/schedule-build-out-editor'
import {createEmptyScheduling} from 'src/service-schedules/schedule-draft-logic'
import {useServiceSchedules} from 'src/service-schedules/use-service-schedules'

import {quoteToDraft} from './quote-adapters'
import type {QuoteDraft, Scheduling} from './quote-builder-logic'
import classes from './quote-scheduling.module.css'
import {
    buildSummaryCounts,
    countMappedRequirements,
    formatQuoteReference,
    generateJobs,
} from './quote-scheduling-logic'
import {useQuote} from './use-quotes'

const FLOW_STEPS = ['Quote accepted', 'Requirements mapped', 'Schedule the build-out']

type QuoteSchedulingPageProps = {
    quoteId: string
}

function StepIndicator() {
    return (
        <Box className={classes['stepper']}>
            {FLOW_STEPS.map((label, index) => {
                const done = index < 2
                const active = index === 2
                const circleState = done
                    ? classes['stepDone']
                    : active
                      ? classes['stepActive']
                      : classes['stepUpcoming']

                return (
                    <Fragment key={label}>
                        <Box className={classes['step']}>
                            <Box className={`${classes['stepCircle']} ${circleState}`}>
                                {done ? <IconCheck size={16} /> : index + 1}
                            </Box>
                            <Box>
                                <Text
                                    className={`${classes['stepEyebrow']} ${
                                        active ? classes['stepEyebrowActive'] : ''
                                    }`}
                                >
                                    {active
                                        ? `Step ${index + 1} · You are here`
                                        : `Step ${index + 1}`}
                                </Text>
                                <Text className={classes['stepLabel']}>{label}</Text>
                            </Box>
                        </Box>
                        {index < FLOW_STEPS.length - 1 && (
                            <Box
                                className={`${classes['stepConnector']} ${
                                    done ? classes['stepConnectorDone'] : ''
                                }`}
                            />
                        )}
                    </Fragment>
                )
            })}
        </Box>
    )
}

export function QuoteSchedulingPage({quoteId}: QuoteSchedulingPageProps) {
    const {data: quoteData, isLoading: isLoadingQuote, isError: isQuoteError} = useQuote(quoteId)
    const {data: customer} = useCustomer(quoteData?.customerId ?? '', {
        enabled: quoteData?.customerId != null,
    })
    const projectId = quoteData?.projectId ?? ''
    const {data: projectSchedules = [], isLoading: isLoadingSchedules} = useServiceSchedules(
        projectId,
        {enabled: projectId.length > 0},
    )

    const {data: workerTypes = [], isLoading: isLoadingWorkerTypes} = useWorkerTypes()
    const {data: rentalAssetTypes = [], isLoading: isLoadingRentalAssetTypes} =
        useRentalAssetTypes()

    const [initialized, setInitialized] = useState(false)
    const [quote, setQuote] = useState<QuoteDraft | null>(null)
    const [draftScheduling, setDraftScheduling] = useState<Scheduling | null>(null)
    const [reference, setReference] = useState('')

    const hasPersistedSchedule = projectSchedules.length > 0

    useEffect(() => {
        if (initialized || isLoadingQuote || quoteData == null) {
            return
        }

        if (projectId.length > 0 && isLoadingSchedules) {
            return
        }

        if (quoteData.customerId != null && customer == null) {
            return
        }

        setQuote(quoteToDraft(quoteData))
        setReference(formatQuoteReference(quoteData, customer?.name ?? null))
        if (!hasPersistedSchedule) {
            setDraftScheduling(createEmptyScheduling())
        }
        setInitialized(true)
    }, [
        customer,
        hasPersistedSchedule,
        initialized,
        isLoadingQuote,
        isLoadingSchedules,
        projectId,
        quoteData,
    ])

    const workerTypeNames = useMemo(
        () => new Map(workerTypes.map((type) => [type.id, type.name])),
        [workerTypes],
    )
    const equipmentTypeNames = useMemo(
        () => new Map(rentalAssetTypes.map((type) => [type.id, type.name])),
        [rentalAssetTypes],
    )
    const workerTypeOptions = useMemo(
        () => workerTypes.map((type) => ({value: type.id, label: type.name})),
        [workerTypes],
    )
    const equipmentTypeOptions = useMemo(
        () => rentalAssetTypes.map((type) => ({value: type.id, label: type.name})),
        [rentalAssetTypes],
    )

    const draftGeneratedJobs = useMemo(() => {
        if (quote == null || draftScheduling == null) {
            return []
        }

        return generateJobs(draftScheduling, quote.requirements)
    }, [draftScheduling, quote])

    const draftSummaryCounts = useMemo(
        () => buildSummaryCounts(draftGeneratedJobs),
        [draftGeneratedJobs],
    )

    const mappedRequirementCount = useMemo(
        () => (quote == null ? 0 : countMappedRequirements(quote.requirements)),
        [quote],
    )

    const isLoading =
        isLoadingQuote ||
        (projectId.length > 0 && isLoadingSchedules) ||
        isLoadingWorkerTypes ||
        isLoadingRentalAssetTypes ||
        !initialized ||
        quote == null ||
        (!hasPersistedSchedule && draftScheduling == null)

    const showDraftEditor = projectId.length > 0 && !hasPersistedSchedule && draftScheduling != null

    return (
        <Stack gap="lg">
            <Group justify="space-between" align="flex-start">
                <Stack gap={4}>
                    {reference && (
                        <Text className={classes['headerRef']}>Accepted / {reference}</Text>
                    )}
                    <Group gap="sm">
                        <Title order={1}>How Ops will build this out</Title>
                        <Badge color="green" variant="light" leftSection={<IconCheck size={12} />}>
                            Accepted
                        </Badge>
                    </Group>
                    <Text size="sm" c="dimmed">
                        {hasPersistedSchedule
                            ? 'Service schedule handed off — Ops owns revisions from here'
                            : 'Draft scheduling — preview only until an order schedule is created'}
                    </Text>
                </Stack>

                {!isLoading && showDraftEditor ? (
                    <BuildOutSummaryStats
                        jobCount={draftSummaryCounts.jobs}
                        unitCount={draftSummaryCounts.units}
                        crewCount={draftSummaryCounts.crew}
                    />
                ) : null}
            </Group>

            <StepIndicator />

            <Button
                component={Link}
                to="/quotes/$quoteId/requirements"
                params={{quoteId}}
                variant="subtle"
                size="compact-sm"
                w="fit-content"
            >
                Back to requirements
            </Button>

            {isQuoteError ? (
                <Alert color="red" title="Could not load quote">
                    Failed to load this quote. Return to the quotes list and try again.
                </Alert>
            ) : null}

            {isLoading ? (
                <Group justify="center" py="xl">
                    <Loader aria-label="Loading scheduling draft" />
                </Group>
            ) : (
                <Stack gap="lg">
                    {!projectId ? (
                        <Alert color="blue" title="Create an order to continue">
                            This quote is not linked to an order yet.{' '}
                            <Text
                                component={Link}
                                to="/jobs/create"
                                search={{quote: quoteId, new: 1}}
                                fw={600}
                                inherit
                            >
                                Create an order from this quote
                            </Text>{' '}
                            to set up service visits.
                        </Alert>
                    ) : hasPersistedSchedule ? (
                        <Alert color="green" title="Schedule handed off to Ops">
                            This order already has{' '}
                            {projectSchedules.length === 1
                                ? 'a service schedule'
                                : `${projectSchedules.length} service schedules`}
                            . Sales can preview requirements here, but schedule revisions happen on
                            the{' '}
                            <Text
                                component={Link}
                                to="/projects/$projectId"
                                params={{projectId}}
                                fw={600}
                                inherit
                            >
                                order page
                            </Text>
                            .
                        </Alert>
                    ) : (
                        <Alert color="blue" title="No service schedule yet">
                            Set up the service visits on the{' '}
                            <Text
                                component={Link}
                                to="/projects/$projectId"
                                params={{projectId}}
                                fw={600}
                                inherit
                            >
                                order page
                            </Text>{' '}
                            to hand the build-out off to Ops.
                        </Alert>
                    )}

                    {mappedRequirementCount === 0 ? (
                        <Alert color="yellow" title="Map requirements before hand-off">
                            Add at least one mapped requirement on the{' '}
                            <Text
                                component={Link}
                                to="/quotes/$quoteId/requirements"
                                params={{quoteId}}
                                fw={600}
                                inherit
                            >
                                requirements page
                            </Text>{' '}
                            before handing off to Ops.
                        </Alert>
                    ) : null}

                    {showDraftEditor ? (
                        <ScheduleBuildOutEditor
                            scheduling={draftScheduling}
                            onSchedulingChange={setDraftScheduling}
                            patternDisabled={false}
                            cadenceDisabled={false}
                            jobs={draftGeneratedJobs}
                            workerTypeNames={workerTypeNames}
                            equipmentTypeNames={equipmentTypeNames}
                            workerTypeOptions={workerTypeOptions}
                            equipmentTypeOptions={equipmentTypeOptions}
                        />
                    ) : null}

                    {showDraftEditor ? (
                        <Group justify="flex-end">
                            <JobsReadySwitch
                                scheduling={draftScheduling}
                                onSchedulingChange={setDraftScheduling}
                            />
                        </Group>
                    ) : null}

                    {!projectId ? (
                        <Box className={classes['handoffBar']}>
                            <Text size="sm" c="dimmed">
                                Create the order to set up service visits
                            </Text>
                            <Button
                                component={Link}
                                to="/jobs/create"
                                search={{quote: quoteId, new: 1}}
                                color="blue"
                            >
                                Create order
                            </Button>
                        </Box>
                    ) : null}
                </Stack>
            )}
        </Stack>
    )
}
